import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import LoaderComponent from 'components/Loader';
import HierarchyWidget from './HierarchyWidget';
import { unwrap, resolveCost } from 'utils/adb-common-util';

interface Row {
  programname: string;
  sector: string;
  project: string;
  consumedservice: string;
  totalCost: any,
  isExpanded?: boolean;
  isExpandedChild?: boolean;
  consumedservices: [];
  children: Row[];
  [key: string]: any;
}

interface DetailedViewProps {
  fetchData?: any;
  setYears?: any;
  detailedTableType: string;
  detailedTableTitle?: string;
  tableColumns?: any[];
  defaultColumnNames: any[];
  getDetailedTableData: any;
  getHierarchyList: any;
  exportDetailCostSummary?: any;
  flattenFn?: (data: any[]) => any[];
  showExport?: boolean;
  totalCost?: number;
}

const HierarchyPanel = (items: any[]) => {
  return (
    <div>
      <HierarchyWidget items={items} />
    </div>
  );
};

const DetailedViewWidget = forwardRef<any, DetailedViewProps>((props, ref) => {
  const {
    fetchData,
    setYears,
    detailedTableType,
    detailedTableTitle,
    tableColumns,
    defaultColumnNames,
    getDetailedTableData,
    getHierarchyList,
    exportDetailCostSummary,
    flattenFn,
    showExport = true,
    totalCost = 0
  } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [detailedViewList, setDetailedViewList] = useState<Row[]>([]);
  const tableExpandList = useRef<Row[]>([]);
  const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
  const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);


  const makePct = (val: number) => {
    if (!totalCost || totalCost === 0) return '0.00%';
    return `${((val / totalCost) * 100).toFixed(2)}%`;
  };

  function collapseChildren(childrenToCollapse: Row[], newRows: Row[]) {
    for (const child of childrenToCollapse) {
      // Find the child in the flat list
      const childIndex = newRows.findIndex((r) => r.id === child.id);
      if (childIndex > -1) {
        // If this child is expanded, recursively collapse its children using its own children property
        if (newRows[childIndex].isExpanded && newRows[childIndex].children && newRows[childIndex].children.length > 0) {
          collapseChildren(newRows[childIndex].children, newRows);
        }
        // Remove the child itself from the flat list
        newRows.splice(childIndex, 1);
      }
    }
  }

  function getImmediateChildrenForRow(row: any): Row[] {
    let childrenRows: Row[] = [];
    const source = Array.isArray(row.values) && row.values.length > 0 ? row.values : [];

    if (detailedTableType?.includes('-rm-detailed-view')) {
      childrenRows = source;
      return childrenRows;
    }

    for (const obj of source) {
      if (obj?.month) {
        const m = unwrap(obj.month);
        const costVal = resolveCost(m);
        childrenRows.push({
          id: `${row.id}-month-${m.month_name}`,
          time_range: m.month_name,
          cost: costVal,
          count: costVal,
          percent_of_total: m.percent_of_total ?? makePct(costVal),
          values: m.children || [],
          isExpanded: false,
          level: (row.level || 0) + 1
        } as any);
        continue;
      }

      const key = Object.keys(obj)[0];
      const itemWrapper = obj[key];
      const item = unwrap(itemWrapper);

      if (!item) continue;

      if (item.week_name) {
        const costVal = resolveCost(item);
        childrenRows.push({
          id: `${row.id}-week-${item.week_name}`,
          time_range: item.week_name,
          cost: costVal,
          count: costVal,
          percent_of_total: item.percent_of_total ?? makePct(costVal),
          values: item.children || [],
          isExpanded: false,
          level: (row.level || 0) + 1
        } as any);
      } else if (item.day_name) {
        const costVal = resolveCost(item);
        childrenRows.push({
          id: `${row.id}-day-${item.day_name}`,
          time_range: item.day_name,
          cost: costVal,
          count: costVal,
          percent_of_total: item.percent_of_total ?? makePct(costVal),
          records: item.records || [],
          isExpanded: false,
          values: [],
          level: (row.level || 0) + 1
        } as any);
      } else {
        const costVal = resolveCost(item);
        childrenRows.push({
          id: `${row.id}-child-${Math.random().toString(36).slice(2, 9)}`,
          time_range: item.name || item.label || key,
          cost: costVal,
          count: costVal,
          percent_of_total: item.percent_of_total ?? makePct(costVal),
          values: item.children || [],
          isExpanded: false,
          level: (row.level || 0) + 1
        } as any);
      }
    }

    return childrenRows;
  }

  async function toggleSubRow(id: string) {
    setDetailedViewList((prev) => {
      const rows = [...prev];
      const rowIndex = rows.findIndex((r: any) => r.id === id);
      const row = rows[rowIndex];
      const newRows = [...rows];
      let children: Row[] = [];

      if (row && !row.isExpanded) {
        const immediate = getImmediateChildrenForRow(row);
        children = immediate.map((x: any) => ({
          ...x,
          isChildren: true,
          isExpanded: false,
          children: []
        }));
        newRows.splice(Number(rowIndex) + 1, 0, ...children);
        newRows[rowIndex] = { ...row, isExpanded: true, children };
      } else {
        // Collapse: recursively remove all descendants
        children = row.children ?? [];
        collapseChildren(children, newRows);
        newRows[rowIndex] = { ...row, isExpanded: false, children: [] };
      }
      return newRows;
    });
  }

  // expose methods to parent via ref if needed
  useImperativeHandle(ref, () => ({
    toggleSubRow: (id: string) => toggleSubRow(id)
  }),);

  const handleSorting = (sortingInfo: any) => {
    setSortingInfo(sortingInfo);
    localStorage.setItem(detailedTableType, JSON.stringify(sortingInfo))
    getDetailedTableData(page, pageSize, sortingInfo);
  };

  const handlePageChange = (event: any, value: number) => {
    getDetailedTableData(value, pageSize, sortingInfo);
  };

  const handlepageSizeChange = (event: any) => {
    getDetailedTableData(page, event.target.value, sortingInfo);
  };

  // Table Data Components
  const loadTableData = async () => {
    setLoading(true);
    try {
      let payload = fetchData?.data?.data ?? [];
      let pageInfo = fetchData?.data?.page ?? {
        currentPage: page,
        pageSize,
        totalElements: 0,
        totalPages: 0
      };

      if (typeof flattenFn === 'function') {
        const flattenedAll = flattenFn(fetchData?.data || []);
        const totalElements = flattenedAll.length;
        const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));
        const currentPage = Math.min(Math.max(1, page), totalPages);
        const start = (currentPage - 1) * pageSize;

        payload = flattenedAll.slice(start, start + pageSize);
        pageInfo = { currentPage, pageSize, totalElements, totalPages };
      }

      setDetailedViewList(payload);
      tableExpandList.current = payload;
      setPaginationData(pageInfo);
      setPage(pageInfo.currentPage ?? page);
      setPageSize(pageInfo.pageSize ?? pageSize);
      const sortingData: any = localStorage.getItem(detailedTableType);
      setSortingInfo(sortingData ? JSON.parse(sortingData) : sortingInfo);

      const uniqueYears = new Set<string>();
      for (const item of payload) {
        for (const key of Object.keys(item || {})) {
          if (![
            'programname', 'sector', 'project', 'consumedservice', 'totalCost',
            'id', 'name', 'values', 'level', 'label', 'programId', 'projectId', 'environment'
          ].includes(key)) {
            uniqueYears.add(key);
          }
        }
      }
      setYears?.(Array.from(uniqueYears).sort((a, b) => a.localeCompare(b)));
    } catch (e) {
      console.error(e);
      setDetailedViewList([]);
      setPaginationData({ totalElements: 0, totalPages: 0 });
      setYears?.([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!fetchData) return;
    loadTableData();
  }, [fetchData])

  const currSortingProp = showExport ? { currSorting: sortingInfo } : {};
  return (
    <Stack>
      <Grid item xs={12}>
        {loading ? (
          <LoaderComponent pt={20} />
        ) : (
          <CustomizedDataGrid
            tableColumns={tableColumns}
            tableData={detailedViewList}
            tableType={detailedTableType}
            defaultColumnNames={defaultColumnNames}
            onSorting={handleSorting}
            {...currSortingProp}
            page={page}
            pageSize={pageSize}
            paginationData={paginationData}
            listLength={detailedViewList.length}
            handlepageSizeChange={handlepageSizeChange}
            handlePageChange={handlePageChange}
            isDateRangeFilter={false}
            isDisabled={false}
            showExport={showExport}
            isRenderPagination={showExport}
            fileName='detailed-view'
            exportTableData={() => exportDetailCostSummary(sortingInfo)}
            title={detailedTableTitle || ''}
            hierarchyRender={() => HierarchyPanel(getHierarchyList(true))}
          />
        )}
      </Grid>
    </Stack>
  );
});
export default DetailedViewWidget;